import { PanelRightIcon } from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"
import { Button } from "~/components/ui/button"
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle
} from "~/components/ui/sheet"
import { useIsMobile } from "~/hooks/useMobile"
import { cn } from "~/lib/utils"

export function DetailsLayout({
	title,
	details,
	header,
	forceSheet = false,
	compactMode = "sheet",
	children
}: {
	title: string
	details: ReactNode
	header: (detailsToggle: ReactNode) => ReactNode
	forceSheet?: boolean
	compactMode?: "sheet" | "inline"
	children: ReactNode | ((compact: boolean) => ReactNode)
}) {
	const isMobile = useIsMobile()
	const [container, setContainer] = useState<HTMLDivElement | null>(null)
	const [hasRoom, setHasRoom] = useState(false)
	const [open, setOpen] = useState(false)
	const useSheet = isMobile || forceSheet || !hasRoom

	useEffect(() => {
		if (!container) return
		const observer = new ResizeObserver(([entry]) => {
			if (!entry) return
			const rem = Number.parseFloat(
				getComputedStyle(document.documentElement).fontSize
			)
			setHasRoom(
				(entry.borderBoxSize[0]?.inlineSize ??
					container.getBoundingClientRect().width) >=
					56 * rem
			)
		})
		observer.observe(container)
		return () => observer.disconnect()
	}, [container])

	return (
		<>
			{header(
				useSheet && compactMode === "sheet" && (
					<Button
						variant="outline"
						size="icon"
						aria-label={title}
						onClick={() => setOpen((value) => !value)}
					>
						<PanelRightIcon className="size-4" />
					</Button>
				)
			)}
			<div
				ref={setContainer}
				className={cn(
					"relative flex min-h-0 min-w-0 flex-1 overflow-hidden",
					!useSheet && "pr-88"
				)}
			>
				<div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
					{typeof children === "function" ? children(useSheet) : children}
				</div>
				{useSheet ? (
					compactMode === "sheet" ? (
						<Sheet open={open} onOpenChange={setOpen}>
							<SheetContent showCloseButton aria-describedby={undefined}>
								<SheetHeader>
									<SheetTitle>{title}</SheetTitle>
								</SheetHeader>
								<div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4">
									{details}
								</div>
							</SheetContent>
						</Sheet>
					) : null
				) : (
					<aside
						aria-label={title}
						className="absolute inset-y-0 right-0 w-88 overflow-x-hidden overflow-y-auto border-l border-stroke bg-background p-4"
					>
						{details}
					</aside>
				)}
			</div>
		</>
	)
}
